import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PoundSterling } from 'lucide-react';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { useFormatting } from '@/hooks/useFormatting';
import { usePermissions } from '@/hooks/usePermissions';
import { getCashDurationTerm } from '@/lib/terminology-config';
import { getDashboardMetrics } from '@/services/metricsService';
import { useDataSourceContext } from '@/contexts/DataSourceContext';
import { CashBreakdownModal } from '@/components/modals/CashBreakdownModal';
import { DEMO_BANK_ACCOUNTS } from '@/data/demo-data';
import { HoverUnderlineSpan } from '@/components/ui/HoverUnderlineSpan';
import type { DashboardMetrics } from '@/services/metricsService';
import { useApp } from '@/contexts/AppContext';
import { useBaseForecast } from '@/hooks/useBaseForecast';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import type { BankAccount } from '@/types';

interface CeoCurrentCashCardProps {
  metrics?: DashboardMetrics;
}

export default function CeoCurrentCashCard({ metrics: metricsProp }: CeoCurrentCashCardProps) {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { dataSources } = useDataSourceContext();
  const [showCashModal, setShowCashModal] = useState(false);
  const { mode } = useApp();
  const { metrics: forecastMetrics, isLoading, error } = useBaseForecast();
  const { bankAccounts: liveBankAccountsApi } = useDashboardMetrics();

  const liveBankAccounts = useMemo((): BankAccount[] => {
    if (mode !== 'live') return [];
    return liveBankAccountsApi.map((account) => ({
      id: String(account.id),
      name: account.name,
      bank: account.bank,
      balance: account.balance,
      currency: account.currencyCode,
      type: account.accountType ?? undefined,
    }));
  }, [mode, liveBankAccountsApi]);

  if (!hasPermission('view_executive_cash_position_card')) return null;

  if (mode === 'live' && isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading cash data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (mode === 'live' && error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  const currentCash = mode === 'live'
    ? (forecastMetrics?.startingCash ?? 0)
    : (metricsProp?.currentCash ?? getDashboardMetrics(dataSources).currentCash);

  const runway = mode === 'live'
    ? (forecastMetrics?.coverageMonths ?? 0)
    : (metricsProp?.runway ?? getDashboardMetrics(dataSources).runway);

  // Traffic light based on runway
  const getBadgeStyle = () => {
    if (runway >= 3) {
      return { bg: 'var(--mc-success-bg)', icon: 'var(--mc-success-icon)' };
    } else if (runway >= 1) {
      return { bg: 'var(--mc-warning-bg)', icon: 'var(--mc-warning-icon)' };
    }
    return { bg: 'var(--mc-error-bg)', icon: 'var(--mc-error-icon)' };
  };

  const badge = getBadgeStyle();

  // Sub-text: "3.4 months" + "coverage"
  const durationTerm = getCashDurationTerm(runway);
  const parts = durationTerm.split(' ');
  const figure = parts.slice(0, 2).join(' '); // "3.4 months"
  const label = parts.slice(2).join(' ');     // "coverage"

  return (
    <>
      <Card className="h-full" style={{ borderLeft: `3px solid ${runway >= 3 ? 'var(--mc-success-border)' : runway >= 1 ? 'var(--mc-warning-border)' : 'var(--mc-error-border)'}` }}>
        <CardContent className="p-4 flex flex-col h-full">
          {/* Top row: title + info icon (left) | icon badge (right) */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-base font-medium text-neutral-800 truncate">Current Cash</span>
              <InfoTooltip
                title="Current Cash"
                content="Your total available cash across all connected bank accounts, as of today."
              />
            </div>
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: badge.bg }}
            >
              <PoundSterling className="h-5 w-5" style={{ color: badge.icon }} />
            </div>
          </div>

          {/* Value */}
          <div className="mt-3">
            <HoverUnderlineSpan
              className="text-2xl font-bold text-neutral-900 tracking-tight cursor-pointer"
              onClick={() => setShowCashModal(true)}
            >
              {formatCurrency(currentCash)}
            </HoverUnderlineSpan>
          </div>

          {/* Sub-text */}
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-xs font-semibold text-neutral-800">{figure}</span>
            <span className="text-xs font-normal text-neutral-800">{label}</span>
          </div>
        </CardContent>
      </Card>

      <CashBreakdownModal
        open={showCashModal}
        onClose={() => setShowCashModal(false)}
        accounts={mode === 'live' ? liveBankAccounts : DEMO_BANK_ACCOUNTS}
        totalCash={currentCash}
      />
    </>
  );
}
